import axios, { AxiosResponse } from 'axios';
import { ApiClient } from '../../api-client/api-client';
import {
  AuthenticatedDownloadRequest,
  AuthenticatedUploadRequest,
  BimTreeNode,
  CropConfig,
  DocumentFileUploadRequest,
  EditFileParams,
  FileCategoryType,
  FileCategoryTypeCompletedBidTabulation,
  FileCategoryTypeDrawings,
  FileCategoryTypeSpecifications,
  FileNode,
  FilePermissionType,
  FileUploadRequestParams,
  GeneratePublicUserFileSystemLinkRequest,
  IOptionalFile,
  S3StagingUploadRequest,
  S3UploadResponse,
  ShareFileSystemObjectRequest,
  ShareFileSystemTarget,
  SimpleFileNode,
} from '../../api-client/autogenerated';
import { downloadFileFromLink, inflateObject } from '../../scripts/utils';
import {
  DESIGN_UPLOAD_CONCURRENCY,
  DESIGN_UPLOAD_PRESIGN_CHUNK,
  MULTI_PART_FILE_SIZE,
} from '../../scripts/constants';
import { Uploader } from '../../scripts/multipart-upload';
import * as Mime from 'mime-types';

export type AuthenticatedUploadRequestWithFiles = {
  file: File;
} & AuthenticatedUploadRequest;

export type ImportFromDesignRequest = {
  ownerId: string;
  fullKey: string;
  fullFileName: string;
  projectId: string;
  isPublic?: boolean;
};

export const uploadS3File = async (
  s3Response: S3UploadResponse,
  file: File,
  onUploadProgress?: (progressEvent: { loaded: number; total?: number }) => void,
) => {
  if (s3Response.uploadLink) {
    await axios.put(s3Response.uploadLink, file, {
      headers: {
        'Content-Type': Mime.lookup(file.name) || 'application/octet-stream',
      },
      onUploadProgress,
    });
  } else if (s3Response.uploadId && s3Response.s3Key) {
    console.log('Uploading using multipart upload');
    const uploader = new Uploader({ file, threadsQuantity: 4 });
    onUploadProgress &&
      uploader.onProgress((progress) => {
        onUploadProgress({ loaded: progress.sent, total: progress.total });
      });
    await uploader.initialize(s3Response.uploadId, s3Response.s3Key, true);
  }
};

export const replaceFileByDocumentIdAndFileId = async (
  documentId: string,
  fileId: string,
  file: File,
  onUploadProgress?: (progressEvent: any) => void,
) => {
  const response = await ApiClient.replaceFileByDocumentIdAndFileId({
    documentId,
    fileId,
    baseFileUploadParams: { useMultiPartUpload: file.size > MULTI_PART_FILE_SIZE },
  });
  await uploadS3File(response.data, file, onUploadProgress);
  return response.data;
};

export const uploadGeneralDocumentFile = async (
  documentId: string,
  params: DocumentFileUploadRequest,
  file: File,
  onUploadProgress?: (progressEvent: any) => void,
) => {
  const res = await ApiClient.insertFileByDocumentId({
    documentId,
    documentFileUploadRequest: params,
  });
  await uploadS3File(res.data, file, onUploadProgress);
  return res.data;
};

export const importGeneralDocumentFileFromDesign = async (
  documentId: string,
  fileType: FileCategoryType,
  params: ImportFromDesignRequest,
) => {
  return ApiClient.insertFileByDocumentId({
    documentId,
    documentFileUploadRequest: { fileType, ...params, useMultiPartUpload: false },
  }).then((res) => res.data);
};

export const uploadToStaging = async (
  params: S3StagingUploadRequest,
  file: File,
  onUploadProgress?: (progressEvent: any) => void,
) => {
  const res = await ApiClient.uploadToStaging({
    s3StagingUploadRequest: params,
  });
  await uploadS3File(res.data, file, onUploadProgress);
  return res.data;
};

export const copyGeneralDocumentFileFromStaging = async (
  documentId: string,
  moveFromStagingS3Key: string,
  fileType: FileCategoryType,
  filename: string,
  description?: string,
) => {
  return ApiClient.insertFileByDocumentId({
    documentId,
    documentFileUploadRequest: {
      fileType,
      moveFromStagingS3Key,
      fullFileName: filename,
      useMultiPartUpload: false,
      description,
    },
  }).then((res) => res.data);
};

export const verifyFileByUrl = async (url: string) => {
  return ApiClient.getFileVerificationByUrl({ url }).then((res) => res.data.isVerified);
};

export const editFileById = async (id: string, file: IOptionalFile) => {
  const res = await ApiClient.editFileById({ fileId: id, iOptionalFile: file });
  return res.data;
};

export const uploadProjectSpecificationFile = async (
  projectId: string,
  params: FileUploadRequestParams,
  file: File,
  onUploadProgress?: (progressEvent: any) => void,
  enableParseSubmittals?: boolean,
) => {
  const response = await ApiClient.getFileUploadLinkByProjectId({
    projectId,
    projectFileUploadRequestParams: {
      fileType: FileCategoryTypeSpecifications.Specifications,
      enableParseSubmittals,
      ...params,
    },
  });
  await uploadS3File(response.data.s3Response, file, onUploadProgress);
  return response.data;
};

export const importProjectSpecificationFileFromDesign = async (
  projectId: string,
  params: ImportFromDesignRequest,
) => {
  return ApiClient.getFileUploadLinkByProjectId({
    projectId,
    projectFileUploadRequestParams: {
      fileType: FileCategoryTypeSpecifications.Specifications,
      fullFileName: params.fullFileName,
      projectId,
      ownerId: params.ownerId,
      fullKey: params.fullKey,
      useMultiPartUpload: false,
    },
  }).then((res) => res.data);
};

export const uploadBidSpecificationFile = async (
  bidSetupId: string,
  params: FileUploadRequestParams,
  file: File,
  onUploadProgress?: (progressEvent: any) => void,
) => {
  const response = await ApiClient.getUploadLinkForSpecificationsByBidSetupId({
    bidSetupId,
    fileUploadRequestParams: params,
  });
  await uploadS3File(response.data.s3Response, file, onUploadProgress);
  return response.data;
};
export const importBidSpecificationFileFromDesign = async (
  bidSetupId: string,
  params: ImportFromDesignRequest,
) => {
  return ApiClient.getUploadLinkForSpecificationsByBidSetupId({
    bidSetupId,
    fileUploadRequestParams: { ...params, useMultiPartUpload: false },
  });
};

export const uploadProjectDrawingsFile = async (
  projectId: string,
  params: FileUploadRequestParams,
  file: File,
  onUploadProgress?: (progressEvent: any) => void,
) => {
  const response = await ApiClient.getFileUploadLinkByProjectId({
    projectId,
    projectFileUploadRequestParams: { fileType: FileCategoryTypeDrawings.Drawings, ...params },
  });
  await uploadS3File(response.data.s3Response, file, onUploadProgress);
  return response.data;
};

export const importProjectDrawingsFileFromDesign = async (
  projectId: string,
  params: ImportFromDesignRequest,
) => {
  return ApiClient.getFileUploadLinkByProjectId({
    projectId,
    projectFileUploadRequestParams: {
      fileType: FileCategoryTypeDrawings.Drawings,
      ...params,
      useMultiPartUpload: false,
    },
  }).then((res) => res.data);
};

export const beginDrawingsPreprocessor = async (projectId: string, fileId: string) => {
  return ApiClient.beginDrawingsPreprocessor({
    drawingsPreprocessorRequest: { projectId, fileId },
  }).then((res) => res.data);
};

export const beginDrawingsParser = async (
  fileId: string,
  originalFileId: string,
  titleBlockCropConfig: CropConfig,
  sheetNameCropConfig: CropConfig,
) => {
  return ApiClient.beginDrawingsParser({
    drawingsParserRequest: { fileId, originalFileId, titleBlockCropConfig, sheetNameCropConfig },
  }).then((res) => res.status);
};

export const beginSpecificationsParser = async (projectId: string, fileId: string) => {
  return ApiClient.beginSpecificationsParser({
    specificationsParserRequest: { projectId, fileId },
  }).then((res) => res.data);
};

export const uploadBidAddendasFile = async (
  bidSetupId: string,
  params: FileUploadRequestParams,
  file: File,
  onUploadProgress?: (progressEvent: any) => void,
) => {
  const response = await ApiClient.getUploadLinkForAddendasByBidSetupId({
    bidSetupId,
    fileUploadRequestParams: params,
  });
  await uploadS3File(response.data, file, onUploadProgress);
  return response.data;
};

export const importBidAddendasFileFromDesign = async (
  bidSetupId: string,
  params: ImportFromDesignRequest,
) => {
  return ApiClient.getUploadLinkForAddendasByBidSetupId({
    bidSetupId,
    fileUploadRequestParams: { ...params, useMultiPartUpload: false },
  }).then((res) => res.data);
};

export const uploadBidDrawingsFile = async (
  bidSetupId: string,
  params: FileUploadRequestParams,
  file: File,
  onUploadProgress?: (progressEvent: any) => void,
) => {
  const response = await ApiClient.getUploadLinkForDrawingsByBidSetupId({
    bidSetupId,
    fileUploadRequestParams: params,
  });
  await uploadS3File(response.data.s3Response, file, onUploadProgress);
  return response.data;
};

export const importBidDrawingsFileFromDesign = async (
  bidSetupId: string,
  params: ImportFromDesignRequest,
) => {
  return ApiClient.getUploadLinkForDrawingsByBidSetupId({
    bidSetupId,
    fileUploadRequestParams: { ...params, useMultiPartUpload: false },
  });
};

export const uploadBidTabulationsFile = async (
  projectId: string,
  params: FileUploadRequestParams,
  file: File,
  onUploadProgress?: (progressEvent: any) => void,
) => {
  const response = await ApiClient.getFileUploadLinkByProjectId({
    projectId,
    projectFileUploadRequestParams: {
      fileType: FileCategoryTypeCompletedBidTabulation.CompletedBidTabulation,
      ...params,
    },
  });
  await uploadS3File(response.data.s3Response, file, onUploadProgress);
  return response.data.s3Response;
};

export const importBidTabulationsFileFromDesign = async (
  projectId: string,
  params: ImportFromDesignRequest,
) => {
  const response = await ApiClient.getFileUploadLinkByProjectId({
    projectId,
    projectFileUploadRequestParams: {
      useMultiPartUpload: false,
      fileType: FileCategoryTypeCompletedBidTabulation.CompletedBidTabulation,
      ...params,
    },
  });

  return response.data.s3Response;
};

export const uploadFilesystemFile = async (
  projectId: string,
  fullKey: string,
  ownerId: string,
  file: File,
  onUploadProgress?: (progressEvent: any) => void,
) => {
  const res = await ApiClient.generateUserFileSystemUploadLink({
    projectId,
    authenticatedUploadRequest: {
      useMultiPartUpload: file.size > MULTI_PART_FILE_SIZE,
      fullKey,
      ownerId,
    },
  });
  await uploadS3File(res.data, file, onUploadProgress);
  return res.data;
};

/**
 * Creates a folder upload's whole directory tree in one request and returns the
 * original->sanitized key mapping the file uploads must be built from.
 */
export const createFilesystemFolders = async (
  projectId: string,
  parentFullKey: string,
  ownerId: string | undefined,
  folderPaths: string[],
) => {
  return ApiClient.createUserFileSystemFolders({
    projectId,
    createFoldersRequest: { parentFullKey, ownerId, folderPaths },
  }).then((res) => res.data);
};

/**
 * Uploads many files: presigned in chunks (the backend caps one request at 200 files) and
 * PUT to S3 through a small concurrency pool, since a strictly sequential loop makes a
 * many-file folder upload take tens of minutes. Progress is reported per finished file.
 */
export const uploadFilesystemFilesBatched = async (
  projectId: string,
  requests: AuthenticatedUploadRequestWithFiles[],
  options: {
    chunkSize?: number;
    concurrency?: number;
    onFileDone?: (file: File, doneCount: number, total: number) => void;
  } = {},
): Promise<(FileNode | undefined)[]> => {
  const {
    chunkSize = DESIGN_UPLOAD_PRESIGN_CHUNK,
    concurrency = DESIGN_UPLOAD_CONCURRENCY,
    onFileDone,
  } = options;
  const nodes: (FileNode | undefined)[] = [];
  let doneCount = 0;
  for (let start = 0; start < requests.length; start += chunkSize) {
    const chunk = requests.slice(start, start + chunkSize);
    const res = await ApiClient.generateMultipleUserFileSystemUploadLink({
      projectId,
      authenticatedUploadRequest: chunk.map(
        ({ fullKey, ownerId, formatted, useMultiPartUpload }) => {
          return { fullKey, ownerId, formatted, useMultiPartUpload };
        },
      ),
    });
    const uploads = res.data.map((s3Response) => async () => {
      const file = s3Response.originalFullKey
        ? chunk.find((request) => request.fullKey === s3Response.originalFullKey)?.file
        : undefined;
      if (file) {
        await uploadS3File(s3Response, file);
        doneCount += 1;
        if (onFileDone) onFileDone(file, doneCount, requests.length);
      }
      nodes.push(s3Response.updatedFileNode);
    });
    let next = 0;
    const workers = Array.from({ length: Math.min(concurrency, uploads.length) }, async () => {
      while (next < uploads.length) {
        const index = next;
        next += 1;
        await uploads[index]();
      }
    });
    await Promise.all(workers);
  }
  return nodes;
};

export const uploadMultipleFilesystemFiles = async (
  projectId: string,
  request: AuthenticatedUploadRequestWithFiles[],
  onUploadProgress?: (progressEvent: any) => void,
  resetUploadProgress?: (nextFile: File) => void,
) => {
  const authenticatedUploadRequest = request.map(
    ({ fullKey, ownerId, formatted, useMultiPartUpload }) => {
      return { fullKey, ownerId, formatted, useMultiPartUpload };
    },
  );
  const res = await ApiClient.generateMultipleUserFileSystemUploadLink({
    projectId,
    authenticatedUploadRequest,
  });
  console.log(`uploadMultipleFilesystemFiles called with response ${JSON.stringify(res.data)}`);
  for (let i = 0; i < res.data.length; i += 1) {
    const s3Response = res.data[i];
    if (s3Response.originalFullKey) {
      const file = request.find((r) => r.fullKey === s3Response.originalFullKey)?.file;
      if (file) {
        if (resetUploadProgress) resetUploadProgress(file);
        await uploadS3File(s3Response, file, onUploadProgress);
      }
    }
  }
  return res.data.map(({ updatedFileNode }) => updatedFileNode);
};

export const getMultipleFilesystemFileDownloadLinks = async (
  projectId: string,
  requests: AuthenticatedDownloadRequest[],
) => {
  return ApiClient.generateMultipleUserFileSystemFileDownloadLink({
    projectId,
    authenticatedDownloadRequest: requests,
  }).then((res) => res.data);
};

export const getFilesystemFileBlob = async (link: string) => {
  return axios
    .get(link, {
      headers: {
        'Content-Type': 'application/octet-stream',
      },
      responseType: 'blob',
    })
    .then((res: AxiosResponse<Blob>) => res.data);
};

/**
 * A short-lived link that shows the file rather than downloading it, for opening it in a new tab.
 */
export const getFilesystemFileViewLink = async (
  projectId: string,
  fullKey: string,
  ownerId: string,
) => {
  const response = await ApiClient.generateUserFileSystemFileDownloadLink({
    projectId,
    authenticatedDownloadRequest: { fullKey, ownerId, isForViewing: true },
  });
  return response.data.downloadLink ?? undefined;
};

export const downloadFilesystemFile = async (
  projectId: string,
  fullName: string,
  fullKey: string,
  isForViewing: boolean,
  ownerId: string,
) => {
  const response = await ApiClient.generateUserFileSystemFileDownloadLink({
    projectId,
    authenticatedDownloadRequest: {
      fullKey,
      ownerId,
      isForViewing,
    },
  });
  await downloadFileFromLink(response.data.downloadLink!, fullName);
};

export const downloadPublicFilesystemFile = async (
  projectId: string,
  fullName: string,
  fullKey: string,
  designFileShareId: string,
  publicAccessKey: string,
) => {
  const response = await ApiClient.generatePublicUserFileSystemFileDownloadLink({
    projectId,
    generatePublicUserFileSystemLinkRequest: {
      fullKey,
      designFileShareId,
      publicAccessKey,
    },
  });
  await downloadFileFromLink(response.data.downloadLink!, fullName);
};

export const getMultiplePublicFilesystemFileDownloadLinks = async (
  projectId: string,
  generatePublicUserFileSystemLinkRequest: GeneratePublicUserFileSystemLinkRequest[],
) => {
  return ApiClient.generateMultiplePublicUserFileSystemFileDownloadLink({
    projectId,
    generatePublicUserFileSystemLinkRequest,
  }).then((res) => res.data);
};

export const getFilesystemFileLink = async (
  projectId: string,
  fullKey: string,
  isForViewing: boolean,
  ownerId: string,
) => {
  return ApiClient.generateUserFileSystemFileDownloadLink({
    projectId,
    authenticatedDownloadRequest: { fullKey, ownerId, isForViewing },
  }).then((res) => res.data.downloadLink);
};

export const getPublicFilesystemFileLink = async (
  projectId: string,
  fullKey: string,
  publicAccessKey: string,
  designFileShareId: string,
) => {
  return ApiClient.generatePublicUserFileSystemFileDownloadLink({
    projectId,
    generatePublicUserFileSystemLinkRequest: { publicAccessKey, fullKey, designFileShareId },
  }).then((res) => res.data.downloadLink);
};

export const createFilesystemFolder = async (
  projectId: string,
  fullKey: string,
  ownerId: string,
) => {
  const response = await ApiClient.createUserFileSystemFolder({
    projectId,
    authenticatedOtherFileSystemRequest: {
      fullKey,
      ownerId,
    },
  });
  return response.data;
};

export const getFilesystemFromProjectId = async (projectId: string) => {
  const request = await ApiClient.listUserFileSystemCompressed({ projectId });
  return inflateObject<FileNode>(request.data);
};

export const getUserFileSystemFileNode = async (
  projectId: string,
  fullKey: string,
  ownerId: string,
) => {
  const request = await ApiClient.getUserFileSystemFileNode({
    projectId,
    authenticatedDownloadRequest: { isForViewing: false, fullKey, ownerId },
  });
  return request.data;
};

/**
 * The fields the API needs to identify a node, and nothing else.
 *
 * A node carries its whole subtree in `children` and one `fileNodePermissions` entry per share -
 * and a folder shared with the project's users inherits an entry per user, so on a large project
 * that array alone runs to hundreds of kilobytes. The API reads none of it: it re-reads the shares
 * from the database. Sent anyway, it pushed the request past the JSON body limit, and rename and
 * delete failed on exactly the folders that were shared the most widely, with the body parser's
 * "request entity too large" surfacing as a server error.
 */
const toFileNodeRequest = (fileNode: FileNode): FileNode => ({
  projectId: fileNode.projectId,
  fullKey: fileNode.fullKey,
  ownerId: fileNode.ownerId,
  type: fileNode.type,
  relativeKey: fileNode.relativeKey,
});

export const modifyUserFileSystemNode = async (
  projectId: string,
  fileNode: FileNode,
  newName: string,
) => {
  const res = await ApiClient.modifyUserFileSystemNode({
    projectId,
    modifyUserFileSystemNodeRequest: {
      fileNode: toFileNodeRequest(fileNode),
      newName,
    },
  });
  return res.data;
};

export const deleteUserFileSystemNode = (projectId: string, fileNode: FileNode) => {
  return ApiClient.deleteUserFileSystemNode({
    projectId,
    body: {
      fileNode: toFileNodeRequest(fileNode),
    },
  });
};

export const getPublicUserFileSystemFileNode = async (
  projectId: string,
  designFileShareId: string,
  publicAccessKey: string,
) => {
  const request = await ApiClient.getPublicUserFileSystemFileNode({
    projectId,
    designFileShareId,
    publicAccessKey,
  });
  return request.data;
};

export const shareUserFileSystemObject = async (
  projectId: string,
  fileNodeKey: string,
  targetType: ShareFileSystemTarget,
  targetParams: {
    target?: string[];
  },
  filePermissions: FilePermissionType[],
) => {
  const { target } = targetParams;

  if (targetType != ShareFileSystemTarget.Everyone && !target)
    throw new Error('Target Ids must be defined for sharing a user file system object');

  const request = await ApiClient.shareUserFileSystemObject({
    projectId,
    shareFileSystemObjectRequest: {
      fileNodeKey,
      targetType,
      ...(target && { target }),
      filePermissions: filePermissions as FilePermissionType[],
    },
  });
  return request.data;
};

export const shareMultipleUserFileSystemObject = async (
  projectId: string,
  shareFileSystemObjectRequest: ShareFileSystemObjectRequest[],
) => {
  return ApiClient.shareMultipleUserFileSystemObject({
    projectId,
    shareFileSystemObjectRequest,
  }).then((res) => res.data);
};

export const getBIMTopFolders = async (projectId: string) => {
  const request = await ApiClient.getBIMTopFolders({ centerLineProjectId: projectId });
  return request.data;
};

export const getBIMTreeNode = async (projectId: string, treeNode: BimTreeNode) => {
  const request = await ApiClient.getBIMTreeNode({
    centerLineProjectId: projectId,
    bimTreeNode: treeNode,
  });
  return request.data;
};

export const downloadBIMFile = async (projectId: string, treeNode: BimTreeNode) => {
  const response1 = await ApiClient.generateBimFileDownloadLink({
    centerLineProjectId: projectId,
    bimTreeNode: treeNode,
  });

  await downloadFileFromLink(response1.data.downloadLink!, treeNode.name);
};

export const getZippedDocumentsLink = async (bidSetupId: string) => {
  return ApiClient.getZippedBidDocuments({ bidSetupId }).then((res) => res.data);
};

export const deleteFile = async (documentId: string, fileId: string) => {
  await ApiClient.modifyDocumentById({
    documentId,
    iModifyDocumentRequest: { deleteFileIds: [fileId] },
  });
};

export const updateFile = async (
  documentId: string,
  fileId: string,
  editFileParams: EditFileParams,
) => {
  return ApiClient.editFileByDocumentIdAndFileId({ documentId, fileId, editFileParams });
};

export const sendDirectDesignFileDownloadEmails = async (
  projectId: string,
  fullKey: string,
  ownerId: string,
  emails: string[],
  formatted?: boolean,
) => {
  return ApiClient.sendDirectDesignFileDownloadEmails({
    projectId,
    sendPublicDesignFileDownloadLinkRequest: {
      isForViewing: false,
      fullKey,
      ownerId,
      emails,
      formatted,
    },
  });
};

export const getDirectDesignFileDownloadLink = async (
  projectId: string,
  fullKey: string,
  ownerId: string,
  formatted?: boolean,
) => {
  return ApiClient.getDirectDesignFileDownloadLink({
    projectId,
    authenticatedDownloadRequest: { isForViewing: false, fullKey, ownerId, formatted },
  });
};

export const sendPublicFolderAccessToEveryone = async (
  projectId: string,
  fullKey: string,
  ownerId: string,
) => {
  return ApiClient.sendPublicFolderAccessToEveryone({
    projectId,
    simpleFileNode: {
      fullKey,
      ownerId,
      projectId,
    },
  });
};

export const sendPublicFolderAccessToEmails = async (
  projectId: string,
  fullKey: string,
  ownerId: string,
  emails: string[],
) => {
  return ApiClient.sendPublicFolderAccessToEmails({
    projectId,
    sendPublicLinkToEmailsRequest: {
      emails,
      fileNode: {
        fullKey,
        ownerId,
        projectId,
      },
    },
  });
};

export const getDesignFileActions = async (
  projectId: string,
  fullKey: string,
  ownerUserId: string,
  page: number,
  pageSize = 50,
) => {
  return ApiClient.getDesignFileActionsByProjectAndFullKey({
    projectId,
    fullKey,
    ownerUserId,
    page,
    pageSize,
    orderByColumn: 'createdOn',
    orderByDirection: 'desc',
  }).then((res) => res.data);
};

export const notifyUsersOfAccessToDesignFile = async (
  projectId: string,
  fileNodes: SimpleFileNode[],
  subject: string,
  userIds?: string[],
  userGroupIds?: string[],
  description?: string,
) => {
  return ApiClient.notifyUsersOfAccessToDesignFile({
    projectId,
    notifyUserAccessDesignFileRequest: {
      subject,
      fileNodes,
      userIds,
      userGroupIds,
      description,
    },
  }).then((res) => res.data);
};

export const getTrashByProjectId = (projectId: string) => {
  return ApiClient.getTrashByProjectId({ projectId }).then((res) => res.data);
};

export const getDownloadLinkForTrashItem = (s3Key: string) => {
  return ApiClient.downloadTrashItemByProjectId({ getTrashItemRequest: { s3Key } }).then(
    (res) => res.data.downloadLink,
  );
};

export const downloadTrashItem = async (s3Key: string, fileName: string) => {
  const link = await getDownloadLinkForTrashItem(s3Key);

  await downloadFileFromLink(link, fileName);
};

export const deleteTrashItemsByProjectId = (projectId: string, s3Keys: string[]) => {
  return ApiClient.deleteTrashItemsByProjectId({
    projectId,
    deleteTrashItemsRequest: { s3Keys },
  }).then((res) => res.data);
};

export const emptyTrashByProjectId = (projectId: string) => {
  return ApiClient.emptyTrashByProjectId({ projectId }).then((res) => res.data);
};
